import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentEmployee } from './useEmployees';
import { useActiveSalesSites } from './useSites';
import {
  defaultSelectedSiteIds,
  filterSitesForEmployee,
  isSalesSiteRestricted,
  isSiteFilterLocked,
  resolvePrimarySalesSite,
} from '../utils/employeeSites';
import type { Site } from '../types/site';

/**
 * Lieux de vente accessibles pour l’utilisateur connecté
 * (affectation employé si `any_site` + assignedSiteIds, sinon tous les actifs).
 */
export function useAccessibleSalesSites(businessId?: string) {
  const { currentBusiness } = useAuth();
  const resolvedBusinessId = businessId ?? currentBusiness?.id;
  const { sites: activeSites, isLoading, error, refetch } = useActiveSalesSites(resolvedBusinessId);
  const { data: employee, isLoading: employeeLoading } = useCurrentEmployee();

  const accessibleSites: Site[] = useMemo(
    () => filterSitesForEmployee(activeSites, employee, currentBusiness),
    [activeSites, employee, currentBusiness]
  );

  const restricted = useMemo(
    () => isSalesSiteRestricted(employee, currentBusiness, activeSites),
    [employee, currentBusiness, activeSites]
  );

  const primarySite = useMemo(
    () => resolvePrimarySalesSite(activeSites, employee, currentBusiness),
    [activeSites, employee, currentBusiness]
  );

  const allAccessibleSelectedIds = useMemo(
    () => defaultSelectedSiteIds(accessibleSites),
    [accessibleSites]
  );

  const filterLocked = isSiteFilterLocked(accessibleSites);

  return {
    accessibleSites,
    allAccessibleSelectedIds,
    primarySite,
    restricted,
    filterLocked,
    employee,
    isLoading: isLoading || employeeLoading,
    error,
    refetch,
  };
}
